// app/javascript/pages/Admin/TermsVersions/Index.tsx
import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  ShieldCheck,
  Plus,
  Users,
  CheckCircle2,
  Clock,
  Eye,
  Edit2,
  Check,
  AlertTriangle,
  ExternalLink,
  FileText
} from 'lucide-react'

interface TermsVersionItem {
  id: number
  version_number: string
  title: string
  summary?: string
  is_active: boolean
  activated_at?: string
  created_at: string
  created_by_name: string
  acceptances_count: number
}

interface Stats {
  current_version: string
  total_users: number
  accepted_users: number
  pending_users: number
  total_log_records: number
}

interface Props {
  versions: TermsVersionItem[]
  stats: Stats
}

export default function TermsVersionsIndex({ versions, stats }: Props) {
  const [activatingId, setActivatingId] = useState<number | null>(null)

  const handleActivate = (version: TermsVersionItem) => {
    if (version.is_active) return
    const confirmed = window.confirm(
      `Activate Terms Version ${version.version_number}?\n\nThis will become the active legal agreement. All users who have not yet accepted v${version.version_number} will be presented with the mandatory terms acceptance modal on their next visit.`
    )
    if (!confirmed) return

    setActivatingId(version.id)
    router.post(
      `/admin/terms_versions/${version.id}/activate`,
      {},
      {
        onFinish: () => setActivatingId(null),
      }
    )
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Terms of Service Management
              </h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                Legal & Compliance
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Version, manage, and monitor acceptance of PrepKiya&apos;s legal terms, disclaimers, and user agreements.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href="/terms" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Public /terms Page</span>
              </a>
            </Button>

            <Button asChild size="sm" className="gap-2">
              <Link href="/admin/terms_versions/new">
                <Plus className="w-4 h-4" />
                <span>New Version</span>
              </Link>
            </Button>
          </div>
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Active Terms Version
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-2xl font-black text-foreground">
                    v{stats.current_version}
                  </span>
                  <Badge variant="default" className="text-[10px] uppercase font-bold">
                    Enforced
                  </Badge>
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <ShieldCheck className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Accepted Users
                </p>
                <p className="text-2xl font-black text-foreground mt-1">
                  {stats.accepted_users}{' '}
                  <span className="text-xs font-normal text-muted-foreground">
                    / {stats.total_users}
                  </span>
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <CheckCircle2 className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Pending Re-Acceptance
                </p>
                <p className="text-2xl font-black text-foreground mt-1">
                  {stats.pending_users}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-muted border border-border flex items-center justify-center text-muted-foreground">
                <Clock className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-card shadow-xs">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Total Acceptance Logs
                </p>
                <p className="text-2xl font-black text-foreground mt-1">
                  {stats.total_log_records}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                <Users className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notice alert */}
        <div className="p-4 rounded-xl border border-border bg-muted/40 flex items-start gap-3 text-xs text-muted-foreground">
          <AlertTriangle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-foreground">Zero-Drift Legal Protection</p>
            <p className="mt-0.5">
              Activating a new terms version will immediately invalidate prior acceptances for users who have not yet agreed to the new version. On their next request, they will be required to accept before resuming full access.
            </p>
          </div>
        </div>

        {/* Terms Versions Table */}
        <Card className="border-border bg-card shadow-xs">
          <CardHeader className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-bold text-foreground">
                  Version History & Audits
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground mt-0.5">
                  Chronological records of all published and drafted Terms of Service.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-xs font-semibold uppercase text-muted-foreground border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Version</th>
                    <th className="px-6 py-3">Title & Summary</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Effective Date</th>
                    <th className="px-6 py-3">Acceptances</th>
                    <th className="px-6 py-3">Created By</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {versions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                        No terms versions recorded yet.
                      </td>
                    </tr>
                  ) : (
                    versions.map((v) => (
                      <tr key={v.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4 font-mono font-bold text-foreground">
                          v{v.version_number}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-semibold text-foreground">{v.title}</div>
                          {v.summary && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                              {v.summary}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {v.is_active ? (
                            <Badge variant="default" className="gap-1 font-semibold text-xs">
                              <Check className="w-3 h-3" />
                              <span>Active</span>
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs font-normal">
                              Inactive Draft
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-muted-foreground">
                          {v.activated_at || 'Not activated'}
                        </td>
                        <td className="px-6 py-4 font-mono text-xs">
                          {v.acceptances_count} logs
                        </td>
                        <td className="px-6 py-4 text-xs text-muted-foreground">
                          {v.created_by_name}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button asChild variant="ghost" size="sm" className="h-8 px-2.5">
                              <Link href={`/admin/terms_versions/${v.id}`} title="View Details & Audit Logs">
                                <Eye className="w-4 h-4" />
                              </Link>
                            </Button>

                            <Button asChild variant="ghost" size="sm" className="h-8 px-2.5">
                              <Link href={`/admin/terms_versions/${v.id}/edit`} title="Edit Terms">
                                <Edit2 className="w-4 h-4" />
                              </Link>
                            </Button>

                            {!v.is_active && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={activatingId === v.id}
                                onClick={() => handleActivate(v)}
                                className="h-8 px-2.5 gap-1.5 text-xs font-medium"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                                <span>Activate</span>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
